import { ReactElement } from 'react';
import {
  MdBlock,
  MdCheckCircle,
  MdErrorOutline,
  MdInfoOutline,
  MdRadioButtonUnchecked,
  MdSchedule
} from 'react-icons/md';
import { REVIEW_UNIT_TITLES, SectionStatus, SectionStatusType } from '../data-qa-constants';
import { WorkspaceModelReview } from '../data-qa-types';
import { modelTypeForProductType } from '../data-qa-utils';
import { ModelCapsule } from '../components/ModelCapsule';

interface ReviewSummarySectionProps {
  workspace: WorkspaceModelReview[];
}

const STATUS_ICONS: Record<SectionStatusType, ReactElement> = {
  [SectionStatus.APPROVED]: <MdCheckCircle className='data-qa-summary-icon--approved' />,
  [SectionStatus.FLAGGED]: <MdErrorOutline className='data-qa-summary-icon--flagged' />,
  [SectionStatus.UNREVIEWED]: (
    <MdRadioButtonUnchecked className='data-qa-summary-icon--unreviewed' />
  )
};

// Mirrors SectionStepper's own campaignBadge -- same three states, same icons, so a
// "Label soon"/"Blocking" pill reads the same wherever it appears in the app.
const CAMPAIGN_ROWS: Record<
  NonNullable<WorkspaceModelReview['campaignStatus']>,
  { icon: ReactElement; title: string }
> = {
  blocking: {
    icon: <MdBlock className='data-qa-summary-icon--campaign-blocking' />,
    title: 'Campaigns — Blocking'
  },
  labelSoon: {
    icon: <MdSchedule className='data-qa-summary-icon--campaign-label-soon' />,
    title: 'Campaigns — Label soon'
  },
  labeled: {
    icon: <MdCheckCircle className='data-qa-summary-icon--approved' />,
    title: 'Campaigns — Labeled'
  }
};

// Exported for unit testing.
export const rollup = (model: WorkspaceModelReview) => {
  const total = model.sections.length;
  const reviewed = model.sections.filter(s => s.status !== SectionStatus.UNREVIEWED).length;
  const anyFlagged = model.sections.some(s => s.status === SectionStatus.FLAGGED);
  // Campaigns' taxonomy-labeling gate isn't one of model.sections (it has no
  // approve/flag status of its own -- see SUMMARY_SECTIONS' own doc comment), so
  // "every section approved" alone doesn't mean the model is actually ready: a model
  // can have all 4 SUMMARY_SECTIONS approved while Campaigns still shows 'Blocking'
  // below. Require it to be resolved too before declaring the model complete.
  // Same for negative values: they block approval of the affected sections, but an
  // approval recorded before the data went negative would otherwise still count.
  const allApproved =
    total > 0 &&
    model.sections.every(s => s.status === SectionStatus.APPROVED) &&
    model.campaignStatus !== 'blocking' &&
    !model.negativeValuesBlocking;
  return { total, reviewed, anyFlagged, allApproved };
};

export const ReviewSummarySection = ({ workspace }: ReviewSummarySectionProps) => {
  const models = workspace.map(model => ({ model, ...rollup(model) }));
  const nModels = models.length;
  const nDone = models.filter(m => m.allApproved).length;
  const allDone = nModels > 0 && nDone === nModels;
  const anyFlagged = models.some(m => m.anyFlagged);

  let banner: { tone: 'ok' | 'warn' | 'info'; icon: ReactElement; title: string; detail: string };
  if (allDone) {
    banner = {
      tone: 'ok',
      icon: <MdCheckCircle />,
      title: 'All models approved',
      detail:
        'Every model in this workspace is fully reviewed — ready for model training / handoff.'
    };
  } else if (anyFlagged) {
    banner = {
      tone: 'warn',
      icon: <MdErrorOutline />,
      title: 'Review required',
      detail: `${nDone}/${nModels} models fully approved; some sections are flagged. Resolve before handoff.`
    };
  } else {
    banner = {
      tone: 'info',
      icon: <MdInfoOutline />,
      title: 'Review in progress',
      detail: `${nDone}/${nModels} models fully approved.`
    };
  }

  return (
    <div className='data-qa-summary'>
      <div className='data-qa-summary__header'>
        <h2 className='data-qa-summary__title'>Review Summary</h2>
        <div className='data-qa-summary__subtitle'>
          Data quality review status across {nModels} model{nModels === 1 ? '' : 's'} · {nDone}/
          {nModels} fully approved
        </div>
      </div>

      <div className={`data-qa-summary__banner data-qa-summary__banner--${banner.tone}`}>
        <span className='data-qa-summary__banner-icon'>{banner.icon}</span>
        <div>
          <div className='data-qa-summary__banner-title'>{banner.title}</div>
          <div className='data-qa-summary__banner-detail'>{banner.detail}</div>
        </div>
      </div>

      {models.map(({ model, total, reviewed, anyFlagged: flagged, allApproved }) => {
        let chip: { tone: 'ok' | 'warn' | 'info'; text: string };
        if (allApproved) {
          chip = { tone: 'ok', text: 'Complete' };
        } else if (flagged) {
          chip = { tone: 'warn', text: 'Flagged' };
        } else {
          chip = { tone: 'info', text: `${reviewed}/${total} reviewed` };
        }
        return (
          <div key={model.configPath} className='data-qa-summary__model'>
            <div className='data-qa-summary__model-head'>
              <ModelCapsule
                modelType={modelTypeForProductType(model.productType)}
                label={model.label}
              />
              <span className='data-qa-summary__model-name'>{model.clientName}</span>
              <span className={`data-qa-summary__chip data-qa-summary__chip--${chip.tone}`}>
                {chip.text}
              </span>
            </div>
            <div className='data-qa-summary__rows'>
              {model.campaignStatus && (
                <div className='data-qa-summary__row'>
                  <span className='data-qa-summary__row-icon'>
                    {CAMPAIGN_ROWS[model.campaignStatus].icon}
                  </span>
                  <span className='data-qa-summary__row-title'>
                    {CAMPAIGN_ROWS[model.campaignStatus].title}
                  </span>
                </div>
              )}
              {model.negativeValuesBlocking && (
                <div className='data-qa-summary__row'>
                  <span className='data-qa-summary__row-icon'>
                    <MdBlock className='data-qa-summary-icon--campaign-blocking' />
                  </span>
                  <span className='data-qa-summary__row-title'>Negative values — Blocking</span>
                </div>
              )}
              {model.sections.map(section => (
                <div key={section.key} className='data-qa-summary__row'>
                  <span className='data-qa-summary__row-icon'>{STATUS_ICONS[section.status]}</span>
                  <span className='data-qa-summary__row-title'>
                    {REVIEW_UNIT_TITLES[section.key]}
                  </span>
                  {section.status === SectionStatus.FLAGGED && section.notes && (
                    <span className='data-qa-summary__row-note'>{section.notes}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};
